/**
 * Sentinel Response™ - Ops Portal Layout
 * Internal layout for Sentinel team with cross-tenant visibility.
 */
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Ticket, Server, Settings, LogOut,
  ChevronDown, Plus, Activity,
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { to: '/sentinel/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/sentinel/tickets', icon: Ticket, label: 'Tickets' },
  { to: '/sentinel/assets', icon: Server, label: 'Assets' },
  { to: '/sentinel/monitoring', icon: Activity, label: 'Monitoring' },
];

export default function SentinelLayout() {
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-gray-950">
      <aside className="w-64 bg-gray-900/95 border-r border-gray-800 flex flex-col">
        {/* Product logo */}
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <img src="/logo-sentinel-icon.svg" alt="Sentinel Response" className="w-10 h-10" />
            <div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold text-white tracking-wider">SENTINEL</span>
                <span className="text-sm font-light text-emerald-400 tracking-wider">RESPONSE</span>
                <span className="text-[7px] text-gray-500 -mt-2">™</span>
              </div>
              <div className="text-[11px] text-gray-500">Operations Portal</div>
            </div>
          </div>
        </div>

        {/* Quick action */}
        <div className="p-3">
          <NavLink
            to="/sentinel/tickets/new"
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-all hover:shadow-lg active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-1 space-y-0.5">
          <div className="text-[10px] font-medium text-gray-600 uppercase tracking-widest px-3 pt-2 pb-2">Operations</div>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? 'bg-emerald-600/15 text-emerald-400 font-medium border-l-[3px] border-emerald-500'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-gray-800">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-800/60 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-medium text-white">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-gray-200 text-sm truncate">{user?.first_name} {user?.last_name}</div>
                <div className="text-xs text-gray-500 capitalize">{user?.role?.replace(/_/g, ' ')}</div>
              </div>
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-20">
                  <div className="px-3 py-2 border-b border-gray-700">
                    <div className="text-xs text-gray-400">{user?.email}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5 capitalize">{user?.role?.replace(/_/g, ' ')}</div>
                  </div>
                  <button onClick={() => setUserMenuOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-300 hover:bg-gray-700 transition-colors">
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                  <button onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-gray-700 transition-colors">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
